import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Label from '../ui/Label';
import StyledCard from '../Cards/StyledCard';
import { Button } from '../Buttons/Button';
import WarningIcon from '../../svg/Staging/WarningIcon';
import SchedulerButton from '../ui/SchedulerButton';
import IconWrapper from '../Cards/IconWrapper';
import { X } from 'lucide-react';
import { ACTION_TYPES } from '../../utility/ActionTypes';
import GlobalDropDown from '../Dropdowns/GlobalDropDown';
import { combineDateWithTime, convertLocalToUTC } from '../../utility/timezoneConverter';
import { useAssessmentPopupBg } from '../../context/ThemeContext';
import { InputField } from '../Form/FormFields';

// const ACTION_TYPES = {
//   DELETE: 'DELETE',
//   EDIT: 'EDIT',
//   DRAFT: 'DRAFT',
//   CLOSE: 'CLOSE',
//   REJECT: 'REJECT',
//   ARCHIVE: 'ARCHIVE',
//   BUDGET: 'BUDGET',
//   MOVE: 'MOVE',
//   ASSESSMENT: 'ASSESSMENT',
//   CAMERAERROR: 'CAMERA ERROR',
//   AUDIOERROR: 'AUDIO ERROR',
//   MEDIAERROR: 'MEDIA ERROR',
//   COMPANYEXIST: 'COMPANY EXIST',
// };

const ACTION_PROPERTIES = {
  [ACTION_TYPES.DELETE]: {
    title: 'Confirm Delete',
    confirmLabel: 'Delete',
    confirmVariant: 'cancel',
  },
  [ACTION_TYPES.EDIT]: {
    title: 'Confirm Edit',
    confirmLabel: 'Edit',
    confirmVariant: 'primary',
  },
  [ACTION_TYPES.DRAFT]: {
    title: 'Confirm Draft',
    confirmLabel: 'Draft',
    confirmVariant: 'primary',
  },
  [ACTION_TYPES.CLOSE]: {
    title: 'Close Job Posting',
    confirmLabel: 'Close',
    confirmVariant: 'cancel',
    requiresReason: true,
  },
  [ACTION_TYPES.REJECT]: {
    title: 'Reject Candidate',
    confirmLabel: 'Reject',
    confirmVariant: 'cancel',
    requiresReason: true,
  },
  [ACTION_TYPES.ARCHIVE]: {
    title: 'Confirm Archive',
    confirmLabel: 'Archive',
    confirmVariant: 'secondary',
  },
  [ACTION_TYPES.MOVE]: {
    title: 'Move to Next Stage',
    confirmLabel: 'Move',
    confirmVariant: 'primary',
  },
  [ACTION_TYPES.ASSESSMENT]: {
    title: 'Start the assessment!',
    mobTitle: 'Take Your Assessment on Desktop',
    confirmLabel: 'Take Assessment',
    confirmVariant: 'primary',
    cancelLabel: 'Cancel',
    message: 'Take the assessment now to get prioritized and improve your likelihood of advancing quickly.',
    mobMessage: 'Open the application on your desktop and take the assessment to get prioritized and improve your chances of advancing quickly.'
  },
  [ACTION_TYPES.CAMERAERROR]: {
    title: 'Camera Access Denied',
    message : "We need access to your camera to proceed. Please allow camera access in your browser settings (address bar) to continue."
  },
  [ACTION_TYPES.AUDIOERROR]: {
    title: 'Audio Access Denied',
    message : "We need access to your microphone to proceed. It seems you've denied permission. Please enable it in your browser settings (address bar)."
  },
  [ACTION_TYPES.MEDIAERROR]: {
    title: 'Media Access Denied',
    message : "We need access to your camera and microphone to proceed. It seems you've denied permission for both, please enable them in your browser settings (address bar)."
  },
  [ACTION_TYPES.COMPANYEXIST]: {
    title: 'This Company Already Exists',
  },
  [ACTION_TYPES.TELEGRAM]: {
    title: 'Connect on Telegram',
    confirmLabel: 'Connect',
    confirmVariant: 'primary',
    cancelLabel: 'Cancel',
    message: 'Connect with us on Telegram and get notified about your job applications and updates.',
  },
  [ACTION_TYPES.PARK]: {
    title: 'Move to Parked',
    confirmLabel: 'Move to Parked',
    confirmVariant: 'cancel',
    requiresReason: true,
  },
};

const CLOSE_REASONS = [
  { value: 'Hired', label: 'Hired' },
  { value: 'Lack of suitable candidates', label: 'Lack of suitable candidates' },
  { value: 'Budget Constraints', label: 'Budget Constraints' },
  { value: 'Changes in business needs', label: 'Changes in business needs' },
  { value: 'Dont want more entries', label: 'Dont want more entries' },
];

export const REJECTION_REASONS = [
  {
    label: "Candidate's scores did not meet the criteria",
    value: "Candidate's scores did not meet the criteria",
    email: true,
  },
  {
    label: "Candidate has reapplied but was previously rejected",
    value: "Candidate has reapplied but was previously rejected",
    email: false,
  },
  {
    label: "Candidate's portfolio/work samples did not meet expectations",
    value: "Candidate's portfolio/work samples did not meet expectations",
    email: false,
  },
  {
    label: "Candidate displayed unprofessional or bad attitude",
    value: "Candidate displayed unprofessional or bad attitude",
    email: false,
  },
  {
    label: "Candidate's skills do not align with job requirements",
    value: "Candidate's skills do not align with job requirements",
    email: false,
  },
  {
    label: "Candidate withdrew their application",
    value: "Candidate withdrew their application",
    email: false,
  },
  {
    label: "Candidate requested an unrealistic salary",
    value: "Candidate requested an unrealistic salary",
    email: false,
  },
  {
    label: "Candidate was unresponsive to communication",
    value: "Candidate was unresponsive to communication",
    email: false,
  },
  {
    label: "Candidate provided misleading or false information",
    value: "Candidate provided misleading or false information",
    email: false,
  },
];

export const PARK_REASONS = [
  {
    label: "Invalid/broken portfolio URL",
    value: "Invalid/broken portfolio URL",
  },
  {
    label: "Already shortlisted by another reviewer",
    value: "Already shortlisted by another reviewer",
  },
  {
    label: "Not reviewed, ran out of time",
    value: "Not reviewed, ran out of time",
  },
  {
    label: "Other",
    value: "Other",
  },
];


const RedWarning = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
      <path d="M12.5 16V12M12.5 8H12.51M22.5 12C22.5 17.5228 18.0228 22 12.5 22C6.97715 22 2.5 17.5228 2.5 12C2.5 6.47715 6.97715 2 12.5 2C18.0228 2 22.5 6.47715 22.5 12Z" stroke="#FF385C" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const Modal = ({
  open,
  onClose,
  isReadyToClose = true,
  actionType,
  onConfirm,
  item,
  customTitle,
  customMessage,
  customConfirmLabel,
  isconfirmButtonDisabled,
  cancelLabel = "Cancel",
  confirmVariant,
  cancelVariant = "secondary",
  children,
  candidateName,
  candidateScore,
  maxScoreOfStage,
  jobTitle,
  companyName,
  closeReason,
  onCloseReasonChange,
  specifiedWidth,
  noCancel = false,
  noConfirm = false,
  useScheduledReject = false
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [parkedReason, setParkedReason] = useState('');
  const [parkedNote, setParkedNote] = useState('');

  const action = ACTION_PROPERTIES[actionType] || {};

  const navigate = useNavigate();

  // Find if it is mobile-screen
  const isMobile = useMediaQuery({
    query: '(max-width: 768px)'
  })

  const title = customTitle || action.title || 'Confirm Action';
  const message = customMessage || action.message || `Are you sure you want to perform this action?`;
  const confirmLabel = customConfirmLabel || action.confirmLabel || 'Confirm';
  const buttonVariant = confirmVariant || action.confirmVariant || 'primary';

  const handleReasonSelect = (reason) => {
    if (actionType === ACTION_TYPES.REJECT) {
      setRejectionReason(reason);
      setShowEmailPreview(true);
    } else if (actionType === ACTION_TYPES.CLOSE) {
      onCloseReasonChange(reason);
    } else if (actionType === ACTION_TYPES.PARK) {
      setParkedReason(reason);
      if (reason !== 'Other') {
        setParkedNote('');
      }
    }
  };
  const handleConfirm = ( scheduledDate, scheduledTime ) => {
    if (actionType === ACTION_TYPES.ASSESSMENT) {
        navigate('/assessment');
    } else if (actionType === ACTION_TYPES.REJECT) {
        if (!rejectionReason) {
            alert('Please select a reason for rejecting the candidate.');
            return;
        }
        if(scheduledDate && scheduledTime){
          onConfirm(item, rejectionReason , convertLocalToUTC(combineDateWithTime(new Date(scheduledDate.toISOString()),scheduledTime.format('HH:mm'))), scheduledTime?.format('HH:mm'));
        }else{
          onConfirm(item, rejectionReason);
        }
    } else if (actionType === ACTION_TYPES.PARK) {
        if (!parkedReason) {
            alert('Please select a reason for parking the candidate.');
            return;
        }
        if (parkedReason === 'Other' && !parkedNote.trim()) {
            alert('Please provide a note for the Other reason.');
            return;
        }
        onConfirm(item, parkedReason, parkedReason === 'Other' ? parkedNote.trim() : null);
    } else if (actionType === ACTION_TYPES.CLOSE) {
        if (!closeReason) {
            alert('Please select a reason for closing the job.');
            return;
        }
        onConfirm(item); // The closeReason is already available in the parent component
    }else if(useScheduledReject){
      if(scheduledDate && scheduledTime){
        onConfirm(item , scheduledDate?.toISOString(), scheduledTime?.format('HH:mm'));
      }else{
        onConfirm(item);
      }
    } else {
        onConfirm?.(item);
    }
   isReadyToClose && onClose();
};

 // Reset rejection states when modal is closed
 useEffect(() => {
  if (!open) {
    setRejectionReason('');
    setShowEmailPreview(false);
    setParkedReason('');
    setParkedNote('');
  }
}, [open]);

const AssessmentPopup = useAssessmentPopupBg()

  if (!open) return null;

  const renderModalContent = () => {
    if (actionType === ACTION_TYPES.ASSESSMENT) {
      return (
        <div className="flex flex-col items-center relative">
          <div onClick={onClose} className=' cursor-pointer md:hidden absolute -top-14 -right-14 bg-background-60 p-1 rounded-xl'>
            <IconWrapper icon={X} size={0} customIconSize={5} />
          </div>
          <div className="mb-4">
            <img
              src={AssessmentPopup}
              alt="Assessment"
              className="h-auto"
              onError={(e) => {
                e.target.src = '/api/placeholder/256/256';
              }}
            />
          </div>
          <span className={(isMobile  ?  "typography-h3" : "typography-h1") + " "}>{isMobile  ? action.mobTitle :title}</span>
          <p className={(isMobile ? "typography-large-p" :  "typography-body mb-6") +" text-font-gray typography-body "}>{isMobile  ? action.mobMessage :message}</p>
        </div>
      );
    }

    if (actionType === ACTION_TYPES.TELEGRAM) {
      return (
        <div className="flex flex-col items-center relative">
          <div onClick={onClose} className=' cursor-pointer md:hidden absolute -top-14 -right-14 bg-background-60 p-1 rounded-xl'>
            <IconWrapper icon={X} size={0} customIconSize={5} />
          </div>
          <div className="mb-4">
            <img
              src={AssessmentPopup}
              alt="Telegram"
              className="h-auto"
              onError={(e) => {
                e.target.src = '/api/placeholder/256/256';
              }}
            />
          </div>
          <span className={(isMobile  ?  "typography-h3" : "typography-h1") + " "}>{title}</span>
          <p className={(isMobile ? "typography-large-p" :  "typography-body mb-6") +" text-font-gray typography-body "}>{message}</p>
        </div>
      );
    }

    if (actionType === ACTION_TYPES.REJECT) {
      return (
        <>
          <h1>{title}</h1>
          <p className="text-font-gray typography-body mb-4">
            Are you sure you want to reject "{candidateName}"?
          </p>

          <Label icon={WarningIcon} text={`The candidate has scored ${candidateScore}`} spanContent={`/${maxScoreOfStage}`} />   
          
            <div className="mt-4">
              {/* <label htmlFor="rejectionReason" className="block typography-body">
                Please provide the reason for rejecting this candidate
              </label> */}
              <GlobalDropDown
              label={'Please provide the reason for rejecting this candidate'} 
              extraStylesForLabel="font-bricolage font-semibold"
              options={REJECTION_REASONS} 
              value={rejectionReason}
              onChange={handleReasonSelect}           
              />
            </div>
          {(showEmailPreview && REJECTION_REASONS.find(reason => reason.value === rejectionReason)?.email )&& (
            <>
              <p className="text-gray-300 mt-4 mb-2">This rejection email will be sent to the candidate</p>
              <div className="bg-background-100 p-4 rounded mb-4 typography-body ">
                <p className="text-font-main">Dear {candidateName},</p>
                <p className="text-font-main mt-2">
                  Thank you for applying for the {jobTitle} position at {companyName}.
                  After careful review, we have decided to move forward with other candidates.
                </p>
                <p className="text-font-main mt-2">
                  We appreciate your interest in our company and wish you all the best in your job search.
                </p>
                <p className="text-font-main mt-2">
                  Best regards,<br />
                  HR Manager<br />
                  {companyName}
                </p>
              </div>
            </>
          )}
        </>
      );
    }

    if (actionType === ACTION_TYPES.CLOSE && action.requiresReason) {
      return (
        <>
          <h1>{title}</h1>
          <p className="text-font-gray typography-body">{message}</p>
          <div className="mt-4">
            <label htmlFor="closeReason" className="block typography-body mb-2">
              Please select a reason for closing this job
            </label>
            <GlobalDropDown
              extraStylesForLabel="hidden"
              value={closeReason}
              onChange={handleReasonSelect}
              options={CLOSE_REASONS}
              />
          </div>
        </>
      );
    }

    if (actionType === ACTION_TYPES.PARK) {
      return (
        <>
          <h1>{title}</h1>
          <p className="text-font-gray typography-body mb-4">
            {customMessage || `Are you sure you want to move "${candidateName}" to Parked?`}
          </p>
          <div className="mt-4">
            <GlobalDropDown
              label={'Please provide the reason for parking this candidate'}
              extraStylesForLabel="font-bricolage font-semibold"
              options={PARK_REASONS}
              value={parkedReason}
              onChange={handleReasonSelect}
            />
          </div>
          {parkedReason === 'Other' && (
            <div className="mt-4">
              <InputField
                id="parkedNote"
                type="text"
                label="Custom note"
                required
                placeholder="Enter a note"
                value={parkedNote}
                onChange={(e) => setParkedNote(e.target.value)}
              />
            </div>
          )}
        </>
      );
    }

    return (
      <>
        <h1>{title}</h1>
        <p className="text-font-gray typography-body">{message}</p>
      </>
    );
  };

  return (
    <div
      onClick={onClose}
      className={((isMobile && actionType === ACTION_TYPES.ASSESSMENT ) ? "px-6 " : "px-4 ") + " fixed z-50 inset-0 flex justify-center items-center bg-background-overlay transition-colors bg-black/20  md:mx-0"}
    >
      <StyledCard
        onClick={(e) => e.stopPropagation()}
        backgroundColor={"bg-background-90 "}
        padding={2}
        extraStyles={"shadow w-full  mx-4 transform transition-transform duration-200 ease-out " + (specifiedWidth ? specifiedWidth : actionType === ACTION_TYPES.BUDGET ? "max-w-xl" : "max-w-lg")}
      >
        <div >
          {renderModalContent()}
          {children}
          <div className="flex justify-end gap-4 mt-4">
            <div className={(isMobile && actionType === ACTION_TYPES.ASSESSMENT ) ? "hidden" : ""}>
              {!noCancel && 
              <Button variant={cancelVariant} onClick={onClose}>
                {cancelLabel}
              </Button>}
            </div>
            {( actionType !== ACTION_TYPES.ASSESSMENT || (!isMobile && actionType === ACTION_TYPES.ASSESSMENT)) 
            && actionType !== ACTION_TYPES.CAMERAERROR 
            && actionType !== ACTION_TYPES.AUDIOERROR 
            && actionType !== ACTION_TYPES.REJECT 
            && !useScheduledReject
            && actionType !== ACTION_TYPES.MEDIAERROR && !noConfirm &&
              <Button
                variant={buttonVariant}
                onClick={handleConfirm}
                disabled={
                  isconfirmButtonDisabled || (actionType === ACTION_TYPES.REJECT && !rejectionReason) ||
                  (actionType === ACTION_TYPES.CLOSE && !closeReason) ||
                  (actionType === ACTION_TYPES.PARK && (!parkedReason || (parkedReason === 'Other' && !parkedNote.trim())))
                }
              >
                {confirmLabel}
              </Button>
            }
            {
             (actionType === ACTION_TYPES.REJECT || useScheduledReject) && 
             <SchedulerButton onConfirm={handleConfirm} disabled={isconfirmButtonDisabled || (useScheduledReject ? false : !rejectionReason)} buttonText={"Reject"}  modalTitle={"Schedule Email"} modalMessage={"Schedule rejection email with specified date and time"} buttonVariant={"cancel"} />
            }
          </div>
        </div>
      </StyledCard>
    </div>
  );
};

export default Modal;